import React, { Component } from 'react';
import moment from 'moment';
import axios from 'axios';
import { authService } from '../../services/authService';

import { Table, Tag, Button, Input, Select, Space, Spin, Modal, message, Divider, Row, Col, Tooltip } from 'antd';
import { DollarOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../../config/api';
import IcdSelect from './IcdSelect';

// Action Items → Billing, modelled on Xifin's "Billing Errors" screens:
//   1. Error Summary by Error Code (one row per error type, accession count)
//   2. accessions with that error (or all)
//   3. "Billing Errors" modal per accession: header (patient, physician,
//      insurance, ordered tests) and the information billing needs from the
//      ordering physician; each line has New Value (ICD-10 picker / authorization # /
//      text) and Upload Documents; Prev/Next/Save/Save And Next.
// Data: GET /connect/action-items (one entry per 10-digit LIS accession).
//
// flaggedAt is ET wall-clock stored as-is → moment.utc; dos/dob are date strings.

export const CATEGORY_LABELS = {
  dx_missing: 'Missing diagnosis',
  dx_screening_only: 'Screening code only',
  dx_invalid: 'Invalid diagnosis',
  dx_mismatch: 'Diagnosis does not support test',
  dx_not_covered: 'Diagnosis not covered',
  medical_necessity: 'Medical necessity',
  no_auth: 'Authorization needed',
  insurance: 'Insurance problem',
  cob: 'Other coverage is primary',
  medical_records: 'Records requested',
  demographics: 'Patient info mismatch',
};
const CATEGORY_COLORS = {
  dx_missing: 'volcano', dx_screening_only: 'volcano', dx_invalid: 'volcano', dx_mismatch: 'volcano',
  dx_not_covered: 'volcano', medical_necessity: 'volcano',
  no_auth: 'gold', insurance: 'geekblue', cob: 'geekblue', demographics: 'geekblue', medical_records: 'purple',
};
const label = (c) => CATEGORY_LABELS[c] || c;
const wall = (v, f = 'MM/DD/YYYY') => (v ? moment.utc(v).format(f) : '');
const instant = (v, f = 'MM/DD/YYYY h:mm A') => (v ? moment(v).format(f) : '');
const patientName = (p) => (p ? [p.lastName, p.firstName].filter(Boolean).join(', ') : '');
const muted = { color: '#888', fontSize: 11, lineHeight: 1.2 };
const SEX = { M: 'Male', F: 'Female', U: 'Unknown' };

class BillingIssues extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      accessions: [],
      total: 0,
      view: 'summary', // summary | list
      category: null,
      search: '',
      openIdx: -1, // index into visible() of the accession in the modal
      answers: {}, // issueId -> { icd10, authNumber, text }
      noteText: '',
      saving: false,
      allTests: false,
      coverage: null, // { issue, loading, policies, filter }
      uploadingFor: null,
    };
    this.fileInput = React.createRef();
  }

  componentDidMount = () => {
    this.loadData();
  }

  componentDidUpdate(prev) {
    if (prev.client?.ClientCode !== this.props.client?.ClientCode) this.loadData();
  }

  request = async (config) => {
    const token = await authService.ensureValidToken();
    if (!token) {
      console.error('Not authenticated');
      throw new Error('Not authenticated');
    }
    return axios({ ...config, headers: { Authorization: `Bearer ${token}` } });
  }

  loadData = () => {
    this.setState({ loading: true });
    this.request({
      method: 'GET',
      url: `${API_BASE_URL}/connect/action-items`,
      params: { clientCode: this.props.client.ClientCode },
    }).then(res => {
      const accessions = res.data.accessions || [];
      this.setState({ accessions, total: res.data.total || 0, loading: false });
      if (this.props.onCount) this.props.onCount(accessions.length);
    }).catch(err => {
      console.log(err);
      message.error(err.response?.data?.message || 'Could not load billing items');
      this.setState({ loading: false });
    });
  }

  // ── list helpers ──
  visible = () => {
    const { accessions, search, category } = this.state;
    const q = search.trim().toLowerCase();
    return accessions.filter(g => {
      if (category && !g.categories.includes(category)) return false;
      if (!q) return true;
      return [g.accession, patientName(g.patient), g.physician?.name, g.insurance?.name,
        ...g.issues.map(i => i.testName), ...g.issues.map(i => i.testCode), ...g.issues.map(i => i.dxCodes), ...g.issues.map(i => i.cpt)]
        .some(v => String(v || '').toLowerCase().includes(q));
    });
  }

  summaryRows = () => {
    const byCat = new Map();
    this.state.accessions.forEach(g => g.categories.forEach(c => {
      if (!byCat.has(c)) byCat.set(c, { category: c, description: g.issues.find(i => i.category === c)?.action || '', n: 0 });
      byCat.get(c).n += 1;
    }));
    return [...byCat.values()].sort((a, b) => b.n - a.n);
  }

  openAccession = (idx) => this.setState({ openIdx: idx, answers: {}, noteText: '', allTests: false, coverage: null });

  closeModal = () => this.setState({ openIdx: -1, answers: {}, noteText: '', coverage: null }, this.pruneAnswered);

  // Answered lines leave the queue (the server filters them the same way on
  // the next load). Done when the modal closes or moves on, not on save, so
  // the office can still add a second document to a request it just answered.
  pruneAnswered = (cb) => this.setState(prev => ({
    accessions: prev.accessions
      .map(g => {
        const issues = g.issues.filter(i => !i.clientResponse || i.status === 'waiting_client');
        return { ...g, issues, categories: [...new Set(issues.map(i => i.category))], waitingOnYou: issues.some(i => i.status === 'waiting_client') };
      })
      .filter(g => g.issues.length),
  }), cb);


  setAnswer = (issueId, patch) => this.setState(prev => ({
    answers: { ...prev.answers, [issueId]: { ...(prev.answers[issueId] || {}), ...patch } },
  }));

  patchAccession = (accession, fn) => this.setState(prev => ({
    accessions: prev.accessions.map(g => (g.accession === accession ? fn(g) : g)),
  }));

  // ── save ──
  // Builds the per-line answers. The office may answer any subset of the
  // tests (one test alone is fine); the rest stay open.
  save = async (thenNext) => {
    const open = this.visible()[this.state.openIdx];
    if (!open) return false;
    const { answers, noteText } = this.state;
    const note = noteText.trim();
    const items = [];
    this.modalRows(open).forEach(i => {
      const a = answers[i.id];
      if (!a) return;
      i.ids.forEach(issueId => {
        const item = { issueId };
        if (i.responseType === 'dx' && a.icd10?.length) item.icd10 = a.icd10;
        if (i.responseType === 'auth' && a.authNumber?.trim()) item.authNumber = a.authNumber.trim();
        if (i.responseType === 'text' && a.text?.trim()) item.text = a.text.trim();
        if (Object.keys(item).length > 1) items.push(item);
      });
    });
    if (!items.length && !note) {
      message.warning('Nothing to save yet.');
      return false;
    }
    this.setState({ saving: true });
    try {
      const res = await this.request({
        method: 'POST',
        url: `${API_BASE_URL}/connect/action-items/${encodeURIComponent(open.accession)}/respond`,
        data: { clientCode: this.props.client.ClientCode, items, note },
      });
      const byIssue = new Map((res.data.responses || []).map(r => [r.issueId, r.clientResponse]));
      this.patchAccession(open.accession, g => ({
        ...g,
        waitingOnYou: false,
        notes: res.data.reply ? [...(g.notes || []), res.data.reply] : g.notes,
        issues: g.issues.map(i => ({
          ...i,
          status: i.status === 'waiting_client' ? 'in_progress' : i.status,
          clientResponse: byIssue.get(i.id) || i.clientResponse,
          clientRespondedAt: byIssue.has(i.id) ? res.data.respondedAt : i.clientRespondedAt,
        })),
      }));
      this.setState({ saving: false, answers: {}, noteText: '' });
      if (res.data.telcorFile) message.success('Saved and sent to Telcor.');
      else if (res.data.telcorError) message.warning('Saved for the billing team, but it could not be sent to Telcor automatically.');
      else message.success('Saved and sent to the billing team.');
      if (thenNext) this.step(1);
      return true;
    } catch (err) {
      console.log(err);
      message.error(err.response?.data?.message || 'Could not save');
      this.setState({ saving: false });
      return false;
    }
  }

  step = (delta) => {
    const target = this.visible()[this.state.openIdx + delta];
    this.pruneAnswered(() => {
      const idx = target ? this.visible().findIndex(g => g.accession === target.accession) : -1;
      if (idx < 0) this.closeModal();
      else this.openAccession(idx);
    });
  }

  // ── covered diagnoses (Telcor NCD/LCD rules) ──
  showCoverage = (issue) => {
    this.setState({ coverage: { issue, loading: true, policies: [], filter: '' } });
    this.request({
      method: 'GET',
      url: `${API_BASE_URL}/connect/action-items/covered-dx`, // not /coverage: the api proxy drops that path segment
      params: { clientCode: this.props.client.ClientCode, cpt: issue.cpt, payer: issue.payerName || '' },
    }).then(res => {
      this.setState(prev => (prev.coverage?.issue.id === issue.id ? { coverage: { ...prev.coverage, loading: false, policies: res.data.policies || [] } } : null));
    }).catch(err => {
      console.log(err);
      message.error(err.response?.data?.message || 'Could not load the coverage policy');
      this.setState({ coverage: null });
    });
  }

  pickCovered = (dx) => {
    const { coverage, answers } = this.state;
    const current = answers[coverage.issue.id]?.icd10 || [];
    if (!current.some(c => c.code === dx.code)) this.setAnswer(coverage.issue.id, { icd10: [...current, { code: dx.code, description: dx.description }] });
    this.setState({ coverage: null });
  }

  // ── documents ──
  startUpload = (ids) => {
    this.setState({ uploadingFor: ids }, () => this.fileInput.current && this.fileInput.current.click());
  }

  onFileChosen = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    const open = this.visible()[this.state.openIdx];
    if (!file || !open) return;
    if (file.size > 15 * 1024 * 1024) {
      message.warning('The file is larger than 15 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const data = String(reader.result).split(',')[1];
      this.request({
        method: 'POST',
        url: `${API_BASE_URL}/connect/action-items/${encodeURIComponent(open.accession)}/documents`,
        data: { clientCode: this.props.client.ClientCode, issueId: this.state.uploadingFor?.[0], issueIds: this.state.uploadingFor, fileName: file.name, mimeType: file.type, data },
      }).then(res => {
        const byIssue = new Map((res.data.responses || []).map(r => [r.issueId, r]));
        this.patchAccession(open.accession, g => ({
          ...g,
          documents: [...(g.documents || []), res.data.document],
          issues: g.issues.map(i => (byIssue.has(i.id) ? { ...i, clientResponse: byIssue.get(i.id).clientResponse, clientRespondedAt: byIssue.get(i.id).clientRespondedAt } : i)),
        }));
        message.success(`${file.name} uploaded`);
      }).catch(err => {
        console.log(err);
        message.error(err.response?.data?.message || 'Could not upload the file');
      });
    };
    reader.readAsDataURL(file);
  }

  download = (doc) => {
    this.request({
      method: 'GET',
      url: `${API_BASE_URL}/connect/action-items/documents/${doc.id}`,
      params: { clientCode: this.props.client.ClientCode },
      responseType: 'blob',
    }).then(res => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName;
      a.click();
      URL.revokeObjectURL(url);
    }).catch(err => {
      console.log(err);
      message.error('Could not download the file');
    });
  }

  // ── modal pieces ──
  renderHeader(open) {
    const { allTests } = this.state;
    const tests = open.orderedTests || [];
    const shown = allTests ? tests : tests.slice(0, 3);
    const ins = open.insurance || {};
    return (
      <Row gutter={[16, 2]} style={{ marginBottom: 8, fontSize: 11, lineHeight: 1.4 }}>
        <Col xs={24} md={8}>
          <div><strong>Accession: </strong>{open.accession}</div>
          <div><strong>Date of Service: </strong>{wall(open.dos)}</div>
          <div><strong>Insurance Co. ID: </strong>{ins.code}</div>
          <div><strong>Insurance Co. Name: </strong>{ins.name}</div>
          <div><strong>Member ID: </strong>{ins.memberId || open.payerMemberId || ''}</div>
          {ins.secondaryName ? <div><strong>Secondary Insurance: </strong>{ins.secondaryName}</div> : null}
        </Col>
        <Col xs={24} md={8}>
          <div><strong>Patient Name: </strong>{patientName(open.patient)}</div>
          <div><strong>Date of Birth: </strong>{open.patient?.dob ? wall(open.patient.dob) : ''}</div>
          <div><strong>Gender: </strong>{SEX[open.patient?.sex] || open.patient?.sex || ''}</div>
        </Col>
        <Col xs={24} md={8}>
          <div><strong>Ordering Physician: </strong>{open.physician?.name || open.providerName}</div>
          <div><strong>Ordering Physician NPI: </strong>{open.physician?.npi}</div>
          <div>
            <strong>Ordered Tests: </strong>
            {shown.map(t => <div key={t.code} style={{ paddingLeft: 12 }}>({t.code}) {t.name}</div>)}
            {tests.length > 3 ? (
              <Button type="link" size="small" style={{ paddingLeft: 12, fontSize: 11, height: 'auto' }} onClick={() => this.setState({ allTests: !allTests })}>
                {allTests ? 'show less' : `+ ${tests.length - 3} more`}
              </Button>
            ) : null}
          </div>
        </Col>
      </Row>
    );
  }

  // Rows of the modal table. Telcor bills a panel as one line per component
  // (24 organisms of a wound PCR…) but the practice ordered ONE test, so lines
  // with the same performed test + error collapse into one row (ids = all the
  // lines it stands for; the answer is saved on every one of them). The
  // practice only ever sees accessions and performed tests, never line counts.
  // Medical records requests collapse per payer: one set of records per payer.
  modalRows = (open) => {
    const rows = [];
    const groups = new Map();
    open.issues.forEach(i => {
      let key;
      if (i.category === 'medical_records') key = `records|${i.payerName || ''}`;
      else if (i.performedTest) key = `test|${i.performedTest.code}|${i.category}|${i.title}`;
      else { rows.push({ ...i, ids: [i.id], lines: [i] }); return; }
      if (!groups.has(key)) {
        const base = { ...i, ids: [], lines: [], cpts: [] };
        if (i.category === 'medical_records') { base.cpt = null; base.testCode = null; base.testName = null; }
        else { base.testCode = i.performedTest.code; base.testName = i.performedTest.name; }
        groups.set(key, base);
        rows.push(base);
      }
      const r = groups.get(key);
      r.ids.push(i.id);
      r.lines.push(i);
      if (i.cpt && !r.cpts.includes(i.cpt)) r.cpts.push(i.cpt);
      if (i.clientResponse && !r.clientResponse) { r.clientResponse = i.clientResponse; r.clientRespondedAt = i.clientRespondedAt; }
      if (i.status === 'waiting_client') r.status = 'waiting_client';
    });
    // A grouped test row carries its first CPT (coverage lookup) and lists the lines on hover.
    rows.forEach(r => { if (r.cpts && r.cpts.length && r.category !== 'medical_records') r.cpt = r.cpts[0]; });
    return rows;
  }

  renderSection(open, title, issues) {
    const { answers } = this.state;
    const ins = open.insurance || {};
    const docsFor = (row) => (open.documents || []).filter(d => row.ids.includes(d.issueId) || (!d.issueId && !row.ids.length));
    const columns = [
      { title: '#', key: 'n', width: 36, render: (_, i, idx) => idx + 1 },
      { title: 'Error Field', dataIndex: 'errorField', key: 'errorField', width: 105 },
      {
        title: 'Related To', key: 'related', width: 150,
        render: (_, i) => {
          if (!(i.responseType !== 'text' && (i.cpt || i.testName))) return <span>Insurance: {ins.name}</span>;
          const many = i.lines && i.lines.length > 1;
          return (
            <span>
              Procedure: {many ? (i.cpts || []).join(', ') : i.cpt}
              {i.testName ? <div style={muted}>({i.testCode}) {i.testName}</div> : null}
            </span>
          );
        },
      },
      {
        title: 'Details', key: 'details',
        render: (_, i) => (
          <div>
            <div>{i.action}</div>
            {i.detail ? <div style={muted}>{i.detail}</div> : null}
            {i.status === 'waiting_client' ? <Tag color="orange">Waiting for your office</Tag> : null}
          </div>
        ),
      },
      {
        title: 'Current Value', key: 'current', width: 150,
        render: (_, i) => {
          if (i.responseType === 'dx') return i.dxCodes ? i.dxCodes.split(',').map(c => c.trim()).filter(Boolean).join(', ') : <span style={muted}>none</span>;
          if (i.category === 'insurance' || i.category === 'cob') return <span>{ins.name}{ins.memberId ? <div style={muted}>{ins.memberId}</div> : null}</span>;
          return null;
        },
      },
      {
        title: 'New Value', key: 'newValue', width: 220,
        render: (_, i) => {
          const a = answers[i.id] || {};
          const sent = i.clientResponse ? <div style={{ ...muted, color: '#389e0d', marginTop: 4 }}>Sent {instant(i.clientRespondedAt, 'MM/DD/YYYY')}: {i.clientResponse.summary}</div> : null;
          if (i.responseType === 'dx') {
            return (
              <div>
                <Space.Compact style={{ width: '100%' }}>
                  <IcdSelect value={a.icd10 || []} onChange={(icd10) => this.setAnswer(i.id, { icd10 })} placeholder="Diagnosis" />
                  {i.cpt ? (
                    <Tooltip title="Covered diagnoses for this test">
                      <Button size="small" icon={<InfoCircleOutlined />} onClick={() => this.showCoverage(i)} />
                    </Tooltip>
                  ) : null}
                </Space.Compact>
                {sent}
              </div>
            );
          }
          if (i.responseType === 'docs') return sent;
          if (i.responseType === 'auth') {
            return (
              <div>
                <Input size="small" placeholder="Authorization #" value={a.authNumber || ''} onChange={(e) => this.setAnswer(i.id, { authNumber: e.target.value })} />
                {sent}
              </div>
            );
          }
          return (
            <div>
              <Input size="small" placeholder={i.errorField} value={a.text || ''} onChange={(e) => this.setAnswer(i.id, { text: e.target.value })} />
              {sent}
            </div>
          );
        },
      },
      {
        title: 'Documents', key: 'docs', width: 125,
        render: (_, i) => (
          <div>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => this.startUpload(i.ids)}>Upload Documents</Button>
            {docsFor(i).map(d => (
              <div key={d.id} style={muted}>
                <Button type="link" size="small" style={{ padding: 0, fontSize: 12 }} onClick={() => this.download(d)}>{d.fileName}</Button>
              </div>
            ))}
          </div>
        ),
      },
    ];
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>{title} {open.accession} ({issues.length})</div>
        <Table
          dataSource={issues}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={false}
          scroll={{ y: 'calc(100vh - 440px)' }}
          locale={{ emptyText: <span style={{ fontStyle: 'italic' }}>No results.</span> }}
        />
      </div>
    );
  }

  renderCoverage() {
    const { coverage } = this.state;
    if (!coverage) return null;
    const { issue, loading, policies, filter } = coverage;
    const q = filter.trim().toLowerCase();
    const insuranceName = this.visible()[this.state.openIdx]?.insurance?.name || '';
    return (
      <Modal
        title="Limited Coverage Policy Details"
        centered
        open
        width={700}
        className="billing-errors-modal"
        footer={null}
        onCancel={() => this.setState({ coverage: null })}
      >
        <div style={{ fontSize: 12, lineHeight: 1.45 }}>
        {loading ? <div style={{ textAlign: 'center' }}><Spin /></div> : policies.length === 0 ? (
          <div>No coverage policy is on file for CPT {issue.cpt}{insuranceName ? ` with ${insuranceName}` : ''}. Select the diagnosis from your patient's medical record.</div>
        ) : policies.map(p => {
          const list = p.diagnoses.filter(d => !q || d.code.toLowerCase().includes(q) || d.description.toLowerCase().includes(q));
          return (
            <div key={p.ruleId}>
              <div style={{ marginBottom: 8 }}>
                The policy <strong>{p.name}</strong>{p.note ? ` (${p.note})` : ''} for CPT {issue.cpt} ({issue.testName}){insuranceName ? ` applied by ${insuranceName}` : ''}
                lists the diagnoses below as supporting medical necessity. Select the one consistent with your patient's medical record.
              </div>
              <Input placeholder="Filter" allowClear size="small" value={filter} onChange={(e) => this.setState(prev => ({ coverage: { ...prev.coverage, filter: e.target.value } }))} style={{ width: 240, marginBottom: 8 }} />
              <div style={muted}>Covered Diagnoses ({list.length})</div>
              <div style={{ maxHeight: 380, overflowY: 'auto', border: '1px solid #f0f0f0', padding: 4 }}>
                {list.map(d => (
                  <div key={d.code} style={{ cursor: 'pointer', padding: '1px 4px' }} onClick={() => this.pickCovered(d)}>
                    <span style={{ fontFamily: 'monospace' }}>{d.code}</span> {d.description}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        </div>
      </Modal>
    );
  }

  renderModal() {
    const { openIdx, saving, noteText } = this.state;
    const list = this.visible();
    const open = list[openIdx];
    if (!open) return null;
    return (
      <Modal
        title="Billing Errors"
        open
        width={Math.min(1100, window.innerWidth - 32)}
        style={{ top: 24 }}
        styles={{ body: { maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', overflowX: 'hidden' } }}
        className="billing-errors-modal"
        onCancel={this.closeModal}
        footer={
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <Button disabled={openIdx <= 0} onClick={() => this.step(-1)}>&lt;&lt; Prev</Button>
              <Button onClick={this.closeModal}>Cancel</Button>
              <Button disabled={openIdx >= list.length - 1} onClick={() => this.step(1)}>Next &gt;&gt;</Button>
            </Space>
            <Space>
              <span style={muted}>{openIdx + 1} of {list.length}</span>
              <Button type="primary" loading={saving} onClick={() => this.save(false)}>Save</Button>
              <Button type="primary" loading={saving} disabled={openIdx >= list.length - 1} onClick={() => this.save(true)}>Save And Next</Button>
            </Space>
          </Space>
        }
      >
        {/* antd components don't inherit inline font sizes and no stylesheet is
            loaded in this app, so the compact type (same sizes as the Custom
            Panel Acknowledgement modal) is scoped here. The small table rule
            needs the full antd selector or antd's own 14px wins. */}
        <style>{`
          .billing-errors-modal .ant-modal-title { font-size: 13px; }
          .billing-errors-modal .ant-col { font-size: inherit; }
          .billing-errors-modal .ant-table-wrapper .ant-table.ant-table-small { font-size: 12px; }
          .billing-errors-modal .ant-table-wrapper .ant-table.ant-table-small .ant-table-cell,
          .billing-errors-modal .ant-table-wrapper .ant-table.ant-table-small .ant-table-thead > tr > th { padding: 3px 6px; font-size: 12px; line-height: 1.45; }
          .billing-errors-modal .ant-btn { font-size: 12px; }
          .billing-errors-modal .ant-input, .billing-errors-modal .ant-input::placeholder,
          .billing-errors-modal .ant-select, .billing-errors-modal .ant-select-selection-item,
          .billing-errors-modal .ant-select-selection-placeholder, .billing-errors-modal .ant-select-selection-search-input { font-size: 12px; }
          .billing-errors-modal .ant-tag { font-size: 11px; line-height: 18px; }
          .billing-errors-dropdown .ant-select-item { font-size: 12px; min-height: 24px; }
        `}</style>
        <div style={{ fontSize: 12, lineHeight: 1.45 }}>
          {this.renderHeader(open)}
          {this.renderSection(open, 'Information Needed from Ordering Physician for Accession', this.modalRows(open))}

        {(open.notes || []).length ? (
          <div style={{ marginBottom: 8 }}>
            {open.notes.map((n, idx) => (
              <div key={idx} style={{ marginBottom: 4 }}>
                <div style={muted}>{instant(n.createdAt)} · {n.author}</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{n.note}</div>
              </div>
            ))}
          </div>
        ) : null}
          <Input.TextArea
            placeholder="Note to the billing team (optional)"
            rows={2}
            maxLength={4000}
            size="small"
            value={noteText}
            onChange={(e) => this.setState({ noteText: e.target.value })}
          />
        </div>
        <input type="file" ref={this.fileInput} style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.heic,.doc,.docx" onChange={this.onFileChosen} />
        {this.renderCoverage()}
      </Modal>
    );
  }

  // ── views ──
  renderSummary() {
    const rows = this.summaryRows();
    const columns = [
      { title: '#', key: 'n', width: 40, render: (_, r, idx) => idx + 1 },
      {
        title: 'Error Code', key: 'code', width: 240,
        render: (_, r) => <Button type="link" size="small" style={{ padding: 0 }} onClick={() => this.setState({ view: 'list', category: r.category })}>{label(r.category)}</Button>,
      },
      { title: 'Error Code Description', dataIndex: 'description', key: 'description' },
      { title: 'Accn Count', dataIndex: 'n', key: 'count', width: 100, align: 'right' },
    ];
    return (
      <Row>
        <Col span={24}>
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <span style={muted}>Select an error to see the accessions with it.</span>
            <Button type="link" size="small" onClick={() => this.setState({ view: 'list', category: null })}>All accessions ({this.state.accessions.length})</Button>
          </Space>
        </Col>
        <Divider/>
        <Col span={24}>
          <div style={{ marginBottom: 4 }}>Error Summary by Error Code ({rows.length})</div>
          <Table
            dataSource={rows}
            columns={columns}
            rowKey="category"
            size="small"
            pagination={false}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1} colSpan={2}><strong>Total Accessions</strong></Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right"><strong>{this.state.accessions.length}</strong></Table.Summary.Cell>
              </Table.Summary.Row>
            )}
            locale={{
              emptyText: (
                <div style={{ padding: '32px 0', color: '#888' }}>
                  <DollarOutlined style={{ fontSize: 24, marginBottom: 8, display: 'block' }} />
                  No billing items currently require your attention
                </div>
              ),
            }}
          />
        </Col>
      </Row>
    );
  }

  renderList() {
    const { category, search, accessions } = this.state;
    const rows = this.visible();
    const categories = [...new Set(accessions.flatMap(g => g.categories))];
    const columns = [
      {
        title: 'Accession', key: 'mobile', responsive: ['xs'],
        render: (_, g) => (
          <div style={{ lineHeight: 1.4 }}>
            <div>{g.accession} {patientName(g.patient)}</div>
            <div style={muted}>DOS {wall(g.dos)} · {g.physician?.name || g.providerName}</div>
            {g.categories.map(c => <Tag key={c} color={CATEGORY_COLORS[c]}>{label(c)}</Tag>)}
          </div>
        ),
      },
      { title: 'Accession', dataIndex: 'accession', key: 'accession', width: 120, responsive: ['sm'] },
      {
        title: 'Patient', key: 'patient', responsive: ['sm'], ellipsis: true,
        render: (_, g) => <div>{patientName(g.patient)}{g.patient?.dob ? <div style={muted}>DOB {wall(g.patient.dob)}</div> : null}</div>,
      },
      { title: 'DOS', dataIndex: 'dos', key: 'dos', width: 100, responsive: ['sm'], render: (v) => wall(v) },
      { title: 'Physician', key: 'physician', responsive: ['lg'], ellipsis: true, render: (_, g) => g.physician?.name || g.providerName },
      { title: 'Insurance', key: 'insurance', responsive: ['md'], ellipsis: true, render: (_, g) => g.insurance?.name },
      {
        title: 'Errors', key: 'needed', responsive: ['sm'],
        render: (_, g) => (
          <>
            {g.categories.map(c => <Tag key={c} color={CATEGORY_COLORS[c]}>{label(c)}</Tag>)}
            {g.waitingOnYou ? <Tag color="orange">Waiting for you</Tag> : null}
            {g.issues.some(i => i.clientResponse) ? <Tag color="green">Answered</Tag> : null}
          </>
        ),
      },
      { title: 'Tests', key: 'n', width: 60, align: 'right', responsive: ['sm'], render: (_, g) => this.modalRows(g).length },
    ];
    return (
      <Row>
        <Col span={24}>
          <Space wrap style={{ width: '100%' }}>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => this.setState({ view: 'summary' })}>Return to Summary</Button>
            <Input
              placeholder="Search"
              allowClear
              value={search}
              onChange={(e) => this.setState({ search: e.target.value })}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Error"
              allowClear
              value={category}
              onChange={(v) => this.setState({ category: v || null })}
              options={categories.map(c => ({ value: c, label: label(c) }))}
              style={{ width: 240 }}
            />
            <Button onClick={this.loadData}>Refresh</Button>
            <span style={muted}>{rows.length} accessions · {rows.reduce((n, g) => n + this.modalRows(g).length, 0)} tests</span>
          </Space>
        </Col>
        <Divider/>
        <Col span={24}>
          <Table
            dataSource={rows}
            columns={columns}
            rowKey="accession"
            size="small"
            pagination={{ pageSize: 25, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}` }}
            onRow={(g) => ({ onClick: () => this.openAccession(rows.indexOf(g)), style: { cursor: 'pointer' } })}
            locale={{ emptyText: <span style={{ fontStyle: 'italic' }}>No results.</span> }}
          />
        </Col>
      </Row>
    );
  }

  render() {
    const { loading, accessions, view } = this.state;
    if (loading && !accessions.length) {
      return (
        <div style={{textAlign:"center"}}>
          <Spin size="large"/>
        </div>
      );
    }
    return (
      <div>
        {view === 'summary' ? this.renderSummary() : this.renderList()}
        {this.renderModal()}
      </div>
    );
  }
}

export default BillingIssues;
